/**
 * Interceptor de Axios para manejo de autenticación
 * Similar al interceptor usado en el sistema de mensajes
 */

import axios from 'axios';

// Configurar interceptor de request para agregar token automáticamente
axios.interceptors.request.use(
  (config) => {
    // Obtener token del localStorage
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('🚨 [AUTH-INTERCEPTOR] Error en request:', error);
    return Promise.reject(error);
  }
);

// Configurar interceptor de respuesta para manejar errores de autenticación
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const { config, response } = error;
    
    // Manejar errores de autenticación
    if (response && response.status === 401) {
      // Token expirado o inválido
      console.log('🔐 [AUTH-INTERCEPTOR] Error 401 detectado, limpiando sesión');
      // Limpiar datos de autenticación
      localStorage.removeItem('token');
      localStorage.removeItem('usuario');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('requiresPasswordChange');
        
      // Redirigir al login solo si no estamos ya en una página de auth
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login') && !currentPath.includes('/auth')) {
        window.location.href = '/login';
      }
    }
    
    // Reintentos automáticos para errores de conexión (similar al sistema de mensajes)
    if (axios.isCancel(error) || !config) {
      return Promise.reject(error);
    }
    
    config.retryCount = config.retryCount || 0;
    const shouldRetry = (
      config.retry && 
      config.retryCount < config.retry &&
      (error.code === 'ECONNABORTED' || 
       error.code === 'ECONNREFUSED' || 
       (error.response && error.response.status >= 500))
    );
    
    if (shouldRetry) {
      config.retryCount += 1;
      const delay = config.retryDelay || 1000;
      const backoff = Math.pow(2, config.retryCount) * delay;
      
      return new Promise(resolve => 
        setTimeout(() => resolve(axios(config)), backoff)
      );
    }
    
    return Promise.reject(error);
  }
);

/**
 * Función para verificar si el usuario está autenticado
 */
export const isAuthenticated = () => {
  const isAuth = localStorage.getItem('isAuthenticated');
  const usuario = localStorage.getItem('usuario');
  return isAuth === 'true' && usuario;
};

/**
 * Función para obtener los datos del usuario autenticado
 */
export const getAuthenticatedUser = () => {
  try {
    const usuario = localStorage.getItem('usuario');
    return usuario ? JSON.parse(usuario) : null;
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Función para cerrar sesión
 */
export const logout = async () => {
  try {
    // Llamar al endpoint de logout del backend
    await axios.get('/logout', { timeout: 5000 });
  } catch (error) {
    console.error('Error al cerrar sesión en el servidor:', error);
  } finally {
    // Limpiar datos locales independientemente del resultado del servidor
    localStorage.removeItem('usuario');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('requiresPasswordChange');
    
    // Redirigir al login
    window.location.href = '/login';
  }
};

/**
 * Función para verificar conexión con el servidor
 */
export const testServerConnection = async () => {
  try {
    const response = await axios.get('/ping', { timeout: 5000 });
    return response.status === 200;
  } catch (error) {
    console.error('Error de conexión con el servidor:', error);
    return false;
  }
};

export default axios;